import { useCallback, useMemo, useState } from 'react'

import { Input } from '../../../Components'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Barra preset per salvare/caricare/eliminare configurazioni di test.
 * I preset vengono persistiti in localStorage.
 * @param {Object} props
 * @param {Function} props.loadPresets - Ritorna l'oggetto preset da localStorage
 * @param {Function} props.savePreset - Salva un preset con il nome dato
 * @param {Function} props.deletePreset - Elimina un preset per nome
 * @param {Function} props.loadPreset - Carica un preset per nome
 * @param {Function} props.resetToDefaults - Resetta la configurazione ai default
 * @param {Function} props.resetConversation - Resetta la conversazione corrente
 */
export function PresetBar({
  loadPresets,
  savePreset,
  deletePreset,
  loadPreset,
  resetToDefaults,
  resetConversation
}) {
  const [selectedPreset, setSelectedPreset] = useState(null)
  const [newPresetName, setNewPresetName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  // I preset vivono in localStorage: questo contatore è il segnale per rileggerli dopo
  // un salvataggio o un'eliminazione (prima la lista restava ferma dopo il delete).
  const [presetsVersion, setPresetsVersion] = useState(0)

  const presetOptions = useMemo(() => {
    const presets = loadPresets()
    return Object.keys(presets).map((name) => ({ label: name, value: name }))
  }, [loadPresets, presetsVersion])

  const handleLoad = useCallback(() => {
    if (!selectedPreset) return
    loadPreset(selectedPreset)
    showToast('success', t.dbcPresetLoaded || 'Preset caricato', selectedPreset)
  }, [selectedPreset, loadPreset])

  const handleSave = useCallback(() => {
    if (!newPresetName.trim()) return
    savePreset(newPresetName.trim())
    setSelectedPreset(newPresetName.trim())
    setNewPresetName('')
    setIsCreating(false)
    setPresetsVersion((v) => v + 1)
    showToast('success', t.dbcPresetSaved || 'Preset salvato', newPresetName.trim())
  }, [newPresetName, savePreset])

  const handleDelete = useCallback(() => {
    if (!selectedPreset) return
    deletePreset(selectedPreset)
    setSelectedPreset(null)
    setPresetsVersion((v) => v + 1)
    showToast('info', t.dbcPresetDeleted || 'Preset eliminato', selectedPreset)
  }, [selectedPreset, deletePreset])

  return (
    <div className="debug-preset-bar">
      <div className="debug-preset-bar__group">
        <i
          className="pi pi-bookmark"
          style={{ fontSize: 14, color: 'var(--ink-500)', flexShrink: 0 }}
        />
        <Input
          id="preset"
          dropdown
          options={presetOptions}
          value={selectedPreset}
          placeholder={t.dbcSelectPreset || 'Seleziona preset...'}
          showClear
          style={{ fontSize: 13, width: '100%', borderRadius: 8, maxWidth: 240 }}
          onChange={({ preset }) => setSelectedPreset(preset)}
        />
        {selectedPreset && (
          <>
            <button className="debug-action-btn" onClick={handleLoad} title={t.dbcLoad || 'Carica'}>
              <i className="pi pi-download" style={{ fontSize: 12 }} />
              <span className="preset-btn-label">{t.dbcLoad || 'Carica'}</span>
            </button>
            <button
              className="debug-action-btn debug-action-btn--danger"
              onClick={handleDelete}
              title={t.dbcDelete || 'Elimina'}
            >
              <i className="pi pi-trash" style={{ fontSize: 12 }} />
              <span className="preset-btn-label">{t.dbcDelete || 'Elimina'}</span>
            </button>
          </>
        )}
      </div>

      <div className="debug-preset-bar__actions">
        {isCreating ? (
          <>
            <input
              type="text"
              value={newPresetName}
              onChange={(e) => setNewPresetName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder={t.dbcPresetName || 'Nome preset...'}
              autoFocus
              className="p-inputtext"
              style={{ fontSize: 13, width: 160 }}
            />
            <button
              className="debug-action-btn debug-action-btn--primary"
              onClick={handleSave}
              title={t.dbcSave || 'Salva'}
            >
              <i className="pi pi-check" style={{ fontSize: 12 }} />
              <span className="preset-btn-label">{t.dbcSave || 'Salva'}</span>
            </button>
            <button
              className="debug-action-btn"
              onClick={() => setIsCreating(false)}
              title={t.dbcCancel || 'Annulla'}
            >
              <i className="pi pi-times" style={{ fontSize: 12 }} />
              <span className="preset-btn-label">{t.dbcCancel || 'Annulla'}</span>
            </button>
          </>
        ) : (
          <button
            className="debug-action-btn debug-action-btn--primary"
            onClick={() => setIsCreating(true)}
            title={t.dbcSavePreset || 'Salva Preset'}
          >
            <i className="pi pi-plus" style={{ fontSize: 12 }} />
            <span className="preset-btn-label">{t.dbcSavePreset || 'Salva Preset'}</span>
          </button>
        )}
        <button
          className="debug-action-btn"
          onClick={resetToDefaults}
          title={t.dbcReset || 'Reset'}
        >
          <i className="pi pi-refresh" style={{ fontSize: 12 }} />
          <span className="preset-btn-label">{t.dbcReset || 'Reset'}</span>
        </button>
        <button
          className="debug-action-btn"
          onClick={resetConversation}
          title={t.dbcClearChat || 'Svuota Chat'}
        >
          <i className="pi pi-eraser" style={{ fontSize: 12 }} />
          <span className="preset-btn-label">{t.dbcClearChat || 'Svuota Chat'}</span>
        </button>
      </div>
    </div>
  )
}
